'use client'

import { createClient } from '../lib/supabase/client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { jwtDecode } from 'jwt-decode'
import { ITokenPayload } from '../models/user'

interface UseRealtimeChatProps {
  userId: string           // ID của user đang chat
  userName: string         // Tên của user đang chat
  userAvatar?: string | null  // Avatar của user đang chat
  isAdmin?: boolean       // User hiện tại có phải admin không
}

export interface ChatMessage {
  id: string
  content: string
  user: {
    name: string
    avatar?: string | null
  }
  isFromAdmin: boolean    // Tin nhắn từ admin hay không
  createdAt: string
}

interface DatabaseMessage {
  id: string
  user_id: string
  sender_id: string
  sender_name: string
  sender_avatar?: string | null
  content: string
  is_from_admin: boolean
  created_at: string
  company_name?: string
}

export function useRealtimeChat({ userId, userName, userAvatar, isAdmin = false }: UseRealtimeChatProps) {
  const supabase = useMemo(() => createClient(), [])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)

  // Key cho localStorage
  const LAST_READ_KEY = `chat_last_read_${userId}`

  // Load initial messages from database
  useEffect(() => {
    const loadMessages = async () => {
      setIsLoading(true)

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

      if (error) {
        console.error('Error loading messages:', error)
      } else if (data) {
        const formattedMessages: ChatMessage[] = data.map((msg: DatabaseMessage) => ({
          id: msg.id,
          content: msg.content,
          user: {
            name: msg.sender_name,
            avatar: msg.sender_avatar,
          },
          isFromAdmin: msg.is_from_admin,
          createdAt: msg.created_at,
        }))
        setMessages(formattedMessages)

        // Đếm tin nhắn chưa đọc từ admin (chỉ cho user thường)
        if (!isAdmin) {
          const lastReadTime = localStorage.getItem(LAST_READ_KEY)
          const unread = formattedMessages.filter((msg) => {
            if (!msg.isFromAdmin) return false // Chỉ đếm tin nhắn từ admin
            if (!lastReadTime) return true // Nếu chưa từng đọc, tất cả đều chưa đọc
            return new Date(msg.createdAt) > new Date(lastReadTime)
          }).length
          setUnreadCount(unread)
        }
      }

      setIsLoading(false)
    }

    loadMessages()
  }, [userId, supabase, isAdmin, LAST_READ_KEY])

  // Subscribe to realtime changes
  useEffect(() => {
    const channel = supabase
      .channel(`messages:user_id=eq.${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `user_id=eq.${userId}`,
        },
        (payload: any) => {
          const newMessage = payload.new as DatabaseMessage
          const formattedMessage: ChatMessage = {
            id: newMessage.id,
            content: newMessage.content,
            user: {
              name: newMessage.sender_name,
              avatar: newMessage.sender_avatar,
            },
            isFromAdmin: newMessage.is_from_admin,
            createdAt: newMessage.created_at,
          }

          // Only add if not already in messages (avoid duplicates)
          setMessages((current) => {
            const exists = current.some(msg => msg.id === formattedMessage.id)
            if (exists) return current
            return [...current, formattedMessage]
          })

          // Tăng unread count nếu tin nhắn từ admin (chỉ cho user thường)
          if (!isAdmin && formattedMessage.isFromAdmin) {
            setUnreadCount((prev) => prev + 1)
          }
        }
      )
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true)
        } else {
          setIsConnected(false)
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, supabase, isAdmin])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!isConnected) return

      // Lấy connectString từ token
      let companyName: string | undefined = undefined
      try {
        const token = localStorage.getItem('token')
        if (token) {
          const decoded = jwtDecode<ITokenPayload>(token)
          companyName = decoded.connectString
        }
      } catch (error) {
        console.error('Error decoding token:', error)
      }

      // Insert into database
      const { error } = await supabase
        .from('messages')
        .insert({
          user_id: userId,           // User đang chat với admin
          sender_id: userId,         // Người gửi (có thể là user hoặc admin)
          sender_name: userName,
          sender_avatar: userAvatar, // Avatar của người gửi
          content: content,
          is_from_admin: isAdmin,    // true nếu admin gửi, false nếu user gửi
          company_name: companyName, // Lưu connectString vào companyName
        })

      if (error) {
        console.error('Error sending message:', error)
      }
    },
    [isConnected, userId, userName, userAvatar, isAdmin, supabase]
  )

  // Mark messages as read (reset unread count)
  const markAsRead = useCallback(() => {
    localStorage.setItem(LAST_READ_KEY, new Date().toISOString())
    setUnreadCount(0)
  }, [LAST_READ_KEY])

  return { messages, sendMessage, isConnected, isLoading, unreadCount, markAsRead }
}
